import React, { useEffect, useId, useRef, useState } from 'react';
import { formatRating } from '../utils/ratings';
import './RatingSlider.css';

export const RatingSlider = ({ value, onChange, label, icon: Icon, color, isDark, averageLine = null, disabled = false, infoText = null, infoKey = 'general', testIdPrefix = 'rating' }) => {
  const [showInfo, setShowInfo] = useState(false);
  const tooltipRef = useRef(null);
  const inputId = useId();
  const percentage = Math.min(100, Math.max(0, ((value - 1) / 9) * 100));
  const palette = { 'text-yellow-500': '#eab308', 'text-blue-500': '#3b82f6', 'text-purple-500': '#8b5cf6', 'text-red-500': '#ef4444' };
  const sliderStyle = {
    '--rating-color': palette[color] || '#8b5cf6',
    '--rating-track': isDark ? 'rgba(255,255,255,0.12)' : 'rgba(73,57,134,0.12)',
    '--rating-fill': `calc(${percentage}% + ${14 - percentage * 0.28}px)`,
  };

  useEffect(() => {
    if (!showInfo) return;

    const handlePointerOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowInfo(false);
      }
    };

    const handleScroll = () => setShowInfo(false);

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showInfo]);
  return (
    <div className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${color}`} />}
          <label htmlFor={inputId} className={`font-medium text-sm leading-snug ${isDark ? 'text-white' : 'text-[#493986]'}`}>{label}</label>
          {infoText && (
            <div className="relative" ref={tooltipRef}>
              <button
                type="button"
                data-testid={`${testIdPrefix}-info-button-${infoKey}`}
                aria-label={`About ${label}`}
                aria-expanded={showInfo}
                onClick={() => setShowInfo((prev) => !prev)}
                className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${isDark ? 'bg-white/15 text-white/80' : 'bg-[#493986]/10 text-[#493986]/70'}`}
              >
                i
              </button>
              {showInfo && (
                <div
                  data-testid={`${testIdPrefix}-info-tooltip-${infoKey}`}
                  className={`absolute right-0 top-7 z-20 w-52 max-w-[calc(100vw-2rem)] p-2 rounded-lg text-xs leading-relaxed shadow-xl ${isDark ? 'bg-[#151522] text-white/90 border border-white/15' : 'bg-white text-[#1a1a2e] border border-[#493986]/15'}`}
                >
                  {infoText}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {averageLine !== null && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-white/10 text-white/60' : 'bg-[#493986]/10 text-[#493986]/60'}`}>
              Avg: {formatRating(averageLine)}
            </span>
          )}
          <output htmlFor={inputId} className={`min-w-12 text-right text-2xl font-bold tabular-nums ${color}`}>{formatRating(value)}</output>
        </div>
      </div>
      <div className="relative" style={sliderStyle}>
        <input
          id={inputId}
          type="range"
          min="1"
          max="10"
          step="0.1"
          value={value}
          disabled={disabled}
          aria-valuetext={`${formatRating(value)} / 10`}
          onChange={event => { if (!disabled) onChange(Number(event.target.value)); }}
          className="rating-range"
        />
        {averageLine !== null && averageLine >= 1 && averageLine <= 10 && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-3 h-5 w-0.5 bg-yellow-500"
            style={{ left: `calc(${((averageLine - 1) / 9) * 100}% + ${14 - ((averageLine - 1) / 9) * 28}px)` }}
          />
        )}
      </div>
      <div className={`flex justify-between text-xs ${isDark ? 'text-white/50' : 'text-[#493986]/60'}`} aria-hidden="true">
        <span>1</span><span>{disabled ? 'Rating' : 'Slide to rate'}</span><span>10</span>
      </div>
    </div>
  );
};

export default RatingSlider;
